import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';


const PlatformConfigurationPanel = () => {
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [newUserRegistration, setNewUserRegistration] = useState(true);
  const [publicChallenges, setPublicChallenges] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem('kkumhyeonlabs:theme') || 'default';
    } catch {
      return 'default';
    }
  });

  const [announcementTitle, setAnnouncementTitle] = useState('');
  const [announcementContent, setAnnouncementContent] = useState('');
  const [announcementType, setAnnouncementType] = useState('info');

  const announcementTypeOptions = [
    { value: 'info', label: '정보' },
    { value: 'warning', label: '경고' },
    { value: 'success', label: '성공' },
    { value: 'error', label: '오류' },
  ];

  const themeOptions = [
    { value: 'default', label: '기본' },
    { value: 'naver', label: '네이버' },
  ];

  const configSections = [
    {
      title: '시스템 설정',
      icon: 'Settings',
      settings: [
        {
          id: 'maintenance',
          label: '유지보수 모드',
          description: '활성화 시 일반 사용자의 접근이 제한됩니다',
          checked: maintenanceMode,
          onChange: setMaintenanceMode,
        },
        {
          id: 'registration',
          label: '신규 가입 허용',
          description: '새로운 사용자의 회원가입을 허용합니다',
          checked: newUserRegistration,
          onChange: setNewUserRegistration,
        },
      ],
    },
    {
      title: '콘텐츠 설정',
      icon: 'FileText',
      settings: [
        {
          id: 'public',
          label: '공개 챌린지 허용',
          description: '사용자가 공개 챌린지를 생성할 수 있습니다',
          checked: publicChallenges,
          onChange: setPublicChallenges,
        },
      ],
    },
    {
      title: '알림 설정',
      icon: 'Bell',
      settings: [
        {
          id: 'email',
          label: '이메일 알림',
          description: '시스템 이메일 알림을 활성화합니다',
          checked: emailNotifications,
          onChange: setEmailNotifications,
        },
      ],
    },
  ];

  const handleSaveConfiguration = () => {
    setShowSaveDialog(true);
  };

  const confirmSave = () => {
    console.log('Configuration saved');
    setShowSaveDialog(false);
  };

  const handlePublishAnnouncement = () => {
    if (announcementTitle && announcementContent) {
      console.log('Publishing announcement:', {
        title: announcementTitle,
        content: announcementContent,
        type: announcementType,
      });
      setAnnouncementTitle('');
      setAnnouncementContent('');
      setAnnouncementType('info');
    }
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 bg-primary/20 rounded-md flex items-center justify-center">
          <Icon name="Sliders" size={20} color="var(--color-primary)" />
        </div>
        <div>
          <h3 className="text-lg font-heading font-semibold text-foreground">플랫폼 설정</h3>
          <p className="text-sm text-muted-foreground caption">시스템 구성 및 기능 관리</p>
        </div>
      </div>

      <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6">
        <div className="flex items-center justify-between mb-6">
          <h4 className="text-base font-heading font-semibold text-foreground">테마</h4>
          <Icon name="Palette" size={20} className="text-muted-foreground" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Select
              label="테마 선택"
              options={themeOptions}
              value={theme}
              onChange={(value) => {
                const next = value || 'default';
                setTheme(next);
                try {
                  localStorage.setItem('kkumhyeonlabs:theme', next);
                } catch {
                  // ignore
                }
                if (next !== 'default') {
                  document.documentElement.dataset.theme = next;
                } else {
                  delete document.documentElement.dataset.theme;
                }
              }}
            />
          </div>
          <div className="rounded-lg border border-border bg-muted/20 p-4">
            <div className="text-sm font-medium text-foreground">네이버 테마</div>
            <div className="text-xs text-muted-foreground caption mt-1">
              문서 제작 도구 톤의 라이트 테마입니다.
            </div>
          </div>
        </div>
      </div>
      <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6">
        <div className="flex items-center justify-between mb-6">
          <h4 className="text-base font-heading font-semibold text-foreground">공지사항 게시</h4>
          <Icon name="Megaphone" size={20} className="text-muted-foreground" />
        </div>

        <div className="space-y-4">
          <Input
            label="공지사항 제목"
            type="text"
            placeholder="공지사항 제목을 입력하세요"
            value={announcementTitle}
            onChange={(e) => setAnnouncementTitle(e?.target?.value)}
            required
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">공지사항 내용</label>
            <textarea
              value={announcementContent}
              onChange={(e) => setAnnouncementContent(e?.target?.value)}
              placeholder="공지사항 내용을 입력하세요"
              rows={4}
              className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-gaming resize-none"
            />
          </div>

          <Select
            label="공지사항 유형"
            options={announcementTypeOptions}
            value={announcementType}
            onChange={setAnnouncementType}
          />

          <Button
            variant="default"
            iconName="Send"
            onClick={handlePublishAnnouncement}
            disabled={!announcementTitle || !announcementContent}
            fullWidth
          >
            공지사항 게시
          </Button>
        </div>
      </div>
      <div className="space-y-4 md:space-y-6">
        {configSections?.map((section, sectionIndex) => (
          <div
            key={sectionIndex}
            className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-primary/20 rounded-md flex items-center justify-center">
                <Icon name={section?.icon} size={20} color="var(--color-primary)" />
              </div>
              <h4 className="text-base font-heading font-semibold text-foreground">{section?.title}</h4>
            </div>

            <div className="space-y-4">
              {section?.settings?.map((setting) => (
                <div
                  key={setting?.id}
                  className="flex items-start justify-between p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-gaming"
                >
                  <div className="flex-1 pr-4">
                    <div className="text-sm font-medium text-foreground mb-1">{setting?.label}</div>
                    <div className="text-xs text-muted-foreground caption">{setting?.description}</div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={setting?.checked}
                      onChange={(e) => setting?.onChange(e?.target?.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-muted rounded-full peer peer-checked:bg-primary peer-focus:ring-2 peer-focus:ring-primary peer-focus:ring-offset-2 transition-gaming">
                      <div className="absolute top-0.5 left-0.5 bg-background w-5 h-5 rounded-full transition-transform peer-checked:translate-x-5" />
                    </div>
                  </label>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6">
        <h4 className="text-base font-heading font-semibold text-foreground mb-4">빠른 작업</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <button className="flex items-center space-x-3 p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-gaming text-left">
            <div className="w-10 h-10 bg-primary/20 rounded-md flex items-center justify-center flex-shrink-0">
              <Icon name="Database" size={20} color="var(--color-primary)" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-foreground">데이터베이스 백업</div>
              <div className="text-xs text-muted-foreground caption">시스템 데이터 백업</div>
            </div>
          </button>

          <button className="flex items-center space-x-3 p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-gaming text-left">
            <div className="w-10 h-10 bg-success/20 rounded-md flex items-center justify-center flex-shrink-0">
              <Icon name="RefreshCw" size={20} color="var(--color-success)" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-foreground">캐시 초기화</div>
              <div className="text-xs text-muted-foreground caption">시스템 캐시 삭제</div>
            </div>
          </button>

          <button className="flex items-center space-x-3 p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-gaming text-left">
            <div className="w-10 h-10 bg-warning/20 rounded-md flex items-center justify-center flex-shrink-0">
              <Icon name="FileText" size={20} color="var(--color-warning)" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-foreground">로그 확인</div>
              <div className="text-xs text-muted-foreground caption">시스템 로그 보기</div>
            </div>
          </button>

          <button className="flex items-center space-x-3 p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-gaming text-left">
            <div className="w-10 h-10 bg-accent/20 rounded-md flex items-center justify-center flex-shrink-0">
              <Icon name="Shield" size={20} color="var(--color-accent)" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-foreground">보안 검사</div>
              <div className="text-xs text-muted-foreground caption">시스템 보안 점검</div>
            </div>
          </button>

          <button className="flex items-center space-x-3 p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-gaming text-left">
            <div className="w-10 h-10 bg-destructive/20 rounded-md flex items-center justify-center flex-shrink-0">
              <Icon name="Trash2" size={20} color="var(--color-destructive)" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-foreground">임시 파일 삭제</div>
              <div className="text-xs text-muted-foreground caption">불필요한 파일 제거</div>
            </div>
          </button>

          <button className="flex items-center space-x-3 p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-gaming text-left">
            <div className="w-10 h-10 bg-primary/20 rounded-md flex items-center justify-center flex-shrink-0">
              <Icon name="Mail" size={20} color="var(--color-primary)" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-foreground">이메일 테스트</div>
              <div className="text-xs text-muted-foreground caption">알림 시스템 확인</div>
            </div>
          </button>
        </div>
      </div>
      <div className="flex items-center justify-end space-x-3">
        <Button variant="outline">초기화</Button>
        <Button variant="default" iconName="Save" onClick={handleSaveConfiguration}>
          설정 저장
        </Button>
      </div>
      {showSaveDialog && (
        <div className="fixed inset-0 z-dialog bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card rounded-lg shadow-gaming-lg p-6 md:p-8 max-w-md w-full">
            <div className="flex items-start space-x-4 mb-6">
              <div className="flex-shrink-0 w-12 h-12 bg-success/20 rounded-md flex items-center justify-center">
                <Icon name="CheckCircle" size={24} color="var(--color-success)" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">설정 저장</h3>
                <p className="text-sm text-muted-foreground">
                  변경된 설정을 저장하시겠습니까? 일부 설정은 즉시 적용됩니다.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
                취소
              </Button>
              <Button variant="default" onClick={confirmSave}>
                저장
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformConfigurationPanel;